import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { debounceTime } from 'rxjs/operators';
import { Group, GroupsApi, GroupTreeNode } from '../../hickory-api';
import { GroupsTreeModel } from './groups-tree.model';

@Component({
  selector: 'app-groups-tree',
  templateUrl: './groups-tree.component.html',
  styleUrls: ['./groups-tree.component.scss'],
  standalone: false
})
export class GroupsTreeComponent implements OnInit {
  private groupSearch: string;
  groupSearchControl = new UntypedFormControl();
  searchResults: GroupsTreeModel[];
  searchInProgress: boolean = false;

  private readonly transformer = (node: GroupsTreeModel, level: number) => {
    node.level = level;
    return node;
  };
  treeControl = new FlatTreeControl<GroupsTreeModel>(
    node => node.level,
    node => node.hasChildren
  );
  treeFlattener = new MatTreeFlattener(
    this.transformer,
    node => node.level,
    node => node.hasChildren,
    node => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  private readonly nodeMap = new Map<number, GroupsTreeModel>(); // cache of all groups stored by groupId

  topGroups: GroupsTreeModel[] = [];
  groupsReady: boolean = false;

  _selectedGroup: Group;
  get selectedGroup(): Group {
    return this._selectedGroup;
  }
  @Input() set selectedGroup(group: Group) {
    this._selectedGroup = group;
  }
  @Output() selectedGroupChange = new EventEmitter<Group>();

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale, private groupsApi: GroupsApi) { }

  hasChild = (_: number, node: GroupsTreeModel) => node.hasChildren;

  convertToTreeModel(groups: GroupTreeNode[]) {
    const newSet: GroupsTreeModel[] = [];
    if (groups && Symbol.iterator in Object(groups)) {
      for (const group of groups) {
        const groupTreeModel = ({ name: group.name, id: group.id, externalId: group.externalId, hasChildren: group.childGroupCount > 0, source: group });
        newSet.push(groupTreeModel);
      }
    }
    return newSet;
  }

  ngOnInit() {
    this.getGroups();

    // Debounce group search and trigger the refresh
    this.groupSearchControl.valueChanges.pipe(debounceTime(300)).subscribe(newValue => {
      this.groupSearch = newValue;
      this.onSearch();
    });

    this.treeControl.expansionModel.changed.subscribe(expandedNodes => {
      const nodes = expandedNodes.added;
      nodes?.forEach(n => this.onExpandNode(n));
    });
  }

  getGroups(node?: GroupsTreeModel) {
    this.groupsReady = this.topGroups.length !== 0;
    this.groupsApi.getGroupTreeNodes(node?.source?.id, null, 1, 500).subscribe(r => {
      const nodes = this.convertToTreeModel(r.items);
      if (node == null) {
        this.topGroups = nodes;
      }
      else {
        node.children = nodes;
        this.treeControl.expand(node);
        node.isLoading = false;
      }
      this.dataSource.data = this.topGroups;
      this.groupsReady = true;
    });
  }

  groupSelected(group: GroupsTreeModel) {
    this.selectedGroup = ({ name: group.name, id: group.id, importedid: group.externalId });
    this.selectedGroupChange.emit(this.selectedGroup);
    this.resetSearch();
  }

  onExpandNode(node: GroupsTreeModel) {
    const group = node?.source;
    if ( // check to see if we need to fetch info on the children:
      node?.children == null || //  - if the DirectoryGroup.children value is not set, OR...
      node?.children?.length !== group.childGroupCount //  - if the node and group child counts don't match
    ) {
      node.isLoading = true;
      this.getGroups(node);

      return false;
    }
  }

  onSearch() {
    const search = this.groupSearch ? this.groupSearch : null;
    if (search) {
      this.searchInProgress = true;
      this.groupsApi.getGroupsV21(null, null, 1, 500, search).subscribe(r => {
        this.searchResults = this.convertToTreeModel(r.items);
        this.searchInProgress = false;
      });
    }
    else {
      this.resetSearch();
    }
  }

  resetSearch() {
    this.groupSearchControl.reset();
    this.groupSearch = null;
    this.searchResults = null;
    this.searchInProgress = false;
  }
}
